/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { CenterContainer } from '@/components/blocks/CenterContainer';
import { RenderContent } from '@/components/blocks/RenderContent';
import { ContentRecommendationSkeletonSX } from '@/components/content/ContentRecommendation/style';
import { useContentRecommendation } from '@/data/Content/ContentRecommendation';
import { useContentEvents } from '@/data/Content/_ContentEvents';
import { ID } from '@/data/types/Basic';
import { WidgetProperties } from '@/data/types/Slot';
import { Box, Skeleton } from '@mui/material';
import React, { FC } from 'react';

const emptyProperties = {} as WidgetProperties;

export const EmptyLoadingElement: FC = () => null;

export const ContentRecommendationSkeleton = () => (
	<CenterContainer sx={ContentRecommendationSkeletonSX}>
		<Skeleton variant="rounded" width="100%" height={30} />
		<Skeleton variant="rounded" width="90%" height={20} />
		<Skeleton variant="rounded" width="80%" height={10} />
	</CenterContainer>
);

export const ContentRecommendation: FC<{
	id: ID;
	properties?: WidgetProperties;
	loadingElement?: JSX.Element;
	withoutFrame?: boolean;
	onLoad?: (content: any) => void;
	emptyElement?: JSX.Element;
}> = ({
	id: _id,
	properties = emptyProperties,
	loadingElement,
	withoutFrame = false,
	onLoad,
	emptyElement,
}) => {
	const { emsName = '' } = properties;
	const { data, loading, title } = useContentRecommendation(emsName);
	const { onContentClick } = useContentEvents();

	if (loading) {
		return loadingElement || <ContentRecommendationSkeleton />;
	}

	if (onLoad) {
		onLoad(data);
	}

	if ((data?.length === 0 || !data) && emptyElement) {
		return emptyElement;
	}

	if (withoutFrame) {
		return (
			<React.Fragment>
				{title?.map((content) => (
					<RenderContent
						key={`${content.id}${content.contentId}`}
						content={content}
						onClick={onContentClick(content)}
					/>
				))}
				{data?.map((content) => (
					<RenderContent
						key={`${content.id}${content.contentId}`}
						content={content}
						onClick={onContentClick(content)}
					/>
				))}
			</React.Fragment>
		);
	}

	return (
		<Box id={`${_id}`}>
			{title?.map((content) => (
				<RenderContent
					key={`${content.id}${content.contentId}`}
					content={content}
					onClick={onContentClick(content)}
				/>
			))}
			{data?.map((content) => (
				<RenderContent
					key={`${content.id}${content.contentId}`}
					content={content}
					onClick={onContentClick(content)}
				/>
			))}
		</Box>
	);
};
